import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatTableDataSource, MatSnackBar } from '@angular/material';
import { GatewayService } from 'src/app/gateway.service';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

/**
 * Modulo "Bitacora tecnico".
 *
 * Filtra por tecnico (idrol=20 activos, con buscador) y rango de fechas
 * (default hoy). Consume el endpoint BITACORA_TECNICO/LISTAR que combina
 * 3 fuentes del SP obtener_bitacora pero por agente:
 *   - Tecnico  (ref_cita_status_hist): acciones del tecnico (status).
 *   - Liberar  (log_liberar_cita): cita liberada por el tecnico.
 *   - Cambio   (log_cambio_cita): reasignaciones donde participo el tecnico.
 */
@Component({
  selector: 'app-bitacora-tecnico',
  templateUrl: './bitacora-tecnico.component.html',
  styleUrls: ['./bitacora-tecnico.component.scss']
})
export class BitacoraTecnicoComponent implements OnInit {

  displayedColumns: string[] = [
    'tipo', 'orden', 'fecha', 'estado', 'banda', 'tecnico', 'usuario', 'comentario', 'localizacion'
  ];
  dataSource = new MatTableDataSource<any>([]);
  total = 0;
  isLoading = false;
  yaBusco = false;

  fechaInicio: string | null = null;
  fechaFin: string | null = null;
  idTecnico: number | null = null;

  // Tecnicos para el filtro + buscador (estilo citas-programadas)
  tecnicos: any[] = [];
  tecnicosFiltrados: any[] = [];
  searchTecnico = '';

  // Color del badge por tipo de fila
  tipoColores: { [key: string]: string } = {
    'Tecnico': '#00838f',
    'Liberar': '#9c27b0',
    'Cambio': '#757575',
  };

  constructor(
    private gateway: GatewayService,
    private snackbar: MatSnackBar,
  ) { }

  ngOnInit() {
    const hoy = this._hoyYYYYMMDD();
    this.fechaInicio = hoy;
    this.fechaFin = hoy;
    this.cargarTecnicos();
  }

  cargarTecnicos(): void {
    this.gateway.get('bitacoraTecnicoTecnicos', {})
      .then((res: any) => {
        this.tecnicos = res && res.rows ? res.rows : [];
        this.tecnicosFiltrados = this.tecnicos;
      })
      .catch(() => {
        this.snackbar.open('Error al cargar técnicos', 'Cerrar', { duration: 3000 });
      });
  }

  filtrarTecnicos(): void {
    const term = (this.searchTecnico || '').trim().toLowerCase();
    if (!term) {
      this.tecnicosFiltrados = this.tecnicos;
    } else {
      this.tecnicosFiltrados = this.tecnicos.filter(t =>
        (t.nombre || '').toLowerCase().indexOf(term) > -1);
    }
  }

  onTecnicoDropdownToggle(opened: boolean): void {
    if (!opened) {
      this.searchTecnico = '';
      this.filtrarTecnicos();
    }
  }

  buscar(): void {
    if (!this.idTecnico) {
      this.snackbar.open('Seleccioná un técnico.', 'Cerrar', { duration: 3000 });
      return;
    }
    if (!this.fechaInicio || !this.fechaFin) {
      this.snackbar.open('Indicá fecha inicio y fecha fin.', 'Cerrar', { duration: 3000 });
      return;
    }
    this.isLoading = true;
    this.gateway.get('bitacoraTecnicoListar', {
      idAgente: this.idTecnico,
      fechaInicio: this.fechaInicio,
      fechaFin: this.fechaFin,
    })
      .then((res: any) => {
        this.isLoading = false;
        this.yaBusco = true;
        this.dataSource.data = res && res.rows ? res.rows : [];
        this.total = this.dataSource.data.length;
      })
      .catch(() => {
        this.isLoading = false;
        this.snackbar.open('Error al cargar la bitácora del técnico.', 'Cerrar', { duration: 3000 });
      });
  }

  limpiar(): void {
    const hoy = this._hoyYYYYMMDD();
    this.fechaInicio = hoy;
    this.fechaFin = hoy;
    this.idTecnico = null;
    this.searchTecnico = '';
    this.filtrarTecnicos();
    this.dataSource.data = [];
    this.total = 0;
    this.yaBusco = false;
  }

  /**
   * Descarga la bitácora actual (la que está en pantalla, ya filtrada por
   * técnico + rango) a un XLSX usando SheetJS + FileSaver.
   */
  descargar(): void {
    const rows = this.dataSource.data || [];
    if (!rows.length) {
      this.snackbar.open('No hay registros para descargar.', 'Cerrar', { duration: 3000 });
      return;
    }
    const pipe = new DatePipe('en-US');
    const fmt = (v: any): string => v ? (pipe.transform(v, 'd/MM/y HH:mm', 'America/Panama') || '') : '';
    const nombreTecnico = (this.tecnicos.find(t => t.idagente === this.idTecnico) || {}).nombre || 'tecnico';

    const data = rows.map(r => ({
      'Tipo': r.tipo || '',
      'Orden': r.orden || '',
      'Fecha': fmt(r.fecha),
      'Estado': r.estado || '',
      'Banda': r.banda || '',
      'Técnico': r.tecnico || '',
      'Usuario': r.usuario || '',
      'Comentario': r.comentario || '',
      'Localización': r.localizacion || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data, { header: Object.keys(data[0]) });
    const workbook = { Sheets: { 'Bitácora': worksheet }, SheetNames: ['Bitácora'] };
    const wbout: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const slug = String(nombreTecnico).trim().replace(/\s+/g, '_');
    FileSaver.saveAs(blob, `bitacora_tecnico_${slug}_${this.fechaInicio}_a_${this.fechaFin}.xlsx`);
  }

  getBadgeColor(tipo: string): string {
    return this.tipoColores[tipo] || '#616161';
  }

  /**
   * Resalta en negrita el separador "->" de los cambios (ant -> nuevo) en la
   * columna Técnico. Escapa el HTML primero (anti-XSS) y solo inyecta <strong>.
   */
  resaltarFlecha(value: any): string {
    if (value == null || value === '') { return ''; }
    const esc = String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return esc.replace(/-&gt;/g, '<strong>-&gt;</strong>');
  }

  isCoords(val: string): boolean {
    if (!val) { return false; }
    return /^-?\d+(\.\d+)?,-?\d+(\.\d+)?$/.test(val.trim());
  }

  getMapsUrl(coords: string): string {
    return `https://www.google.com/maps?q=${coords.trim()}`;
  }

  /** Fecha de hoy local YYYY-MM-DD (compatible con input type=date). */
  private _hoyYYYYMMDD(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
}
